/**
 * The pages of Sequencing settings. Eight sections were gathered into three,
 * one per thing a team sets up: who sends (Mailboxes), how and when they send
 * and who is never written to (Sending), and what happens once mail goes out
 * (Replies and tasks). How Clu writes is Skills > Writing on the Agents page.
 */
export const SEQUENCER_SETTINGS_SECTIONS = [
  'senders',
  'sending',
  'replies',
] as const;

export type SequencerSettingsSection =
  (typeof SEQUENCER_SETTINGS_SECTIONS)[number];

/** What each page is called: in the nav, as its title and in the tab. */
export const SEQUENCER_SETTINGS_LABELS: Record<
  SequencerSettingsSection,
  string
> = {
  senders: 'Mailboxes',
  sending: 'Sending',
  replies: 'Replies and tasks',
};

/**
 * Sections that no longer have a page of their own. Links, bookmarks and the
 * alerts the API has already sent still carry them, so each opens the page
 * that now holds its settings, scrolled to the section that holds them.
 */
const MOVED_SECTIONS: Record<
  string,
  { page: SequencerSettingsSection; anchor: string }
> = {
  schedule: { page: 'sending', anchor: 'hours' },
  safety: { page: 'sending', anchor: 'stop' },
  dnc: { page: 'sending', anchor: 'dnc' },
  calls: { page: 'replies', anchor: 'calls' },
  queue: { page: 'replies', anchor: 'tasks' },
  address: { page: 'sending', anchor: 'footer' },
};

/**
 * Sections that left Sequencing settings for another page: Clu writing is
 * Skills > Writing now. Links, bookmarks and the alerts the API has already
 * sent still carry `section=clu`, so the route sends them there.
 */
export const LEFT_SECTIONS = {
  clu: { to: '/agents', search: { tab: 'skills', view: 'writing' } },
} as const;

export type LeftSequencerSection = keyof typeof LEFT_SECTIONS;

/** The section a `section` search value names that left for another page. */
export function leftSequencerSection(
  value: unknown
): LeftSequencerSection | undefined {
  return typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(LEFT_SECTIONS, value)
    ? (value as LeftSequencerSection)
    : undefined;
}

function moved(value: unknown) {
  return typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(MOVED_SECTIONS, value)
    ? MOVED_SECTIONS[value]
    : undefined;
}

/**
 * The page a `section` search value opens: a current section as it is, a
 * retired one as the page that took its settings, anything else as no
 * section (the first page).
 */
export function sequencerSection(
  value: unknown
): SequencerSettingsSection | undefined {
  if (typeof value !== 'string') return undefined;
  if ((SEQUENCER_SETTINGS_SECTIONS as readonly string[]).includes(value))
    return value as SequencerSettingsSection;
  return moved(value)?.page;
}

/**
 * The section of a page a retired `section` value lands on, so a link to the
 * kill switch still arrives at the kill switch. Undefined for a current page.
 *
 * The router validates a search again after it rewrites the URL to the
 * validated one, and by then `section` is the current page and the retired
 * value is gone. `at` (the anchor the first pass wrote) must survive that
 * pass, or the link lands at the top of the page: so a current page keeps an
 * `at` that names one of its own moved sections, and nothing else.
 */
export function sequencerSectionAnchor(
  value: unknown,
  at?: unknown
): string | undefined {
  const anchor = moved(value)?.anchor;
  if (anchor) return anchor;
  const page = sequencerSection(value);
  if (typeof at !== 'string' || !page) return undefined;
  return Object.values(MOVED_SECTIONS).some(
    (entry) => entry.page === page && entry.anchor === at
  )
    ? at
    : undefined;
}
